import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import {
  Star,
  Calendar,
  MapPin,
  Building2,
  Repeat,
  Bell,
  X,
} from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer";
import { Button } from "../components/ui/button";
import type { Id } from "convex/_generated/dataModel";
import { formatTime } from "@/lib/dateUtils";

/**
 * Shapes returned by `favorites.listMine`.
 *
 * Declared here because `useQuery` results resolve to `any` throughout this
 * project, so nothing flows through from the server's own types. Annotating the
 * rows keeps this file honest about what it is rendering.
 */
type SeriesRow = {
  favoriteId: string;
  patternId: string;
  sampleEventId: string;
  title: string;
  nextDate: string;
  startTime: string;
  venueName: string;
  upcomingCount: number;
};
type EventRow = {
  favoriteId: string;
  eventId: string;
  title: string;
  date: string;
  startTime: string;
  venueName: string;
  isPast: boolean;
};
type VenueRow = {
  favoriteId: string;
  venueId: string;
  name: string;
  address: string;
};
type ReminderRow = {
  reminderId: string;
  eventId: string;
  title: string;
  date: string;
  startTime: string;
  venueName: string;
  token: string;
  patternId: string | null;
  seriesFollowed: boolean;
};
type OrganizerRow = {
  favoriteId: string;
  organizerId: string;
  name: string;
  accountType: string;
  studioAddress?: string;
};

/** "Fri, 18 Sep · 19:00" from an ISO date and a wall-clock time. */
function formatWhen(date: string, startTime: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  const day = Number.isNaN(parsed.getTime())
    ? date
    : parsed.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
  return `${day} · ${formatTime(startTime)}`;
}

interface RowProps {
  favoriteId: string;
  href: string;
  title: string;
  meta: React.ReactNode;
  badge?: React.ReactNode;
  onRemove: (favoriteId: string) => void;
  busy: boolean;
}

function FollowRow({
  favoriteId,
  href,
  title,
  meta,
  badge,
  onRemove,
  busy,
}: RowProps) {
  return (
    <li className="flex items-start justify-between gap-3 p-4 rounded-lg border border-border bg-card">
      <Link href={href} className="min-w-0 flex-1 group">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-foreground group-hover:text-primary transition-colors">
            {title}
          </p>
          {badge}
        </div>
        <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
          {meta}
        </div>
      </Link>
      <button
        onClick={() => onRemove(favoriteId)}
        disabled={busy}
        className="shrink-0 p-1.5 rounded text-muted-foreground hover:text-destructive transition-colors"
        aria-label={`Stop following ${title}`}
        title="Stop following"
      >
        <X className="w-4 h-4" />
      </button>
    </li>
  );
}

function Section({
  icon: Icon,
  title,
  count,
  empty,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  count: number;
  empty: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary" />
        {title}
        {count > 0 && (
          <span className="text-sm font-normal text-muted-foreground">
            ({count})
          </span>
        )}
      </h2>
      {count === 0 ? (
        <p className="text-sm text-muted-foreground px-4 py-6 rounded-lg border border-dashed border-border">
          {empty}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">{children}</ul>
      )}
    </section>
  );
}

/**
 * Everything the signed-in user follows, in one place.
 *
 * Until this page existed the only way to see or undo a follow was to navigate
 * back to each thing and click the control again, which meant nobody had an
 * inventory of what they had signed up for. Registered behind ProtectedRoute:
 * following is account-only.
 */
export function FollowingPage() {
  const data = useQuery(api.favorites.listMine);
  const reminders = useQuery(api.reminders.listMine);
  const remove = useMutation(api.favorites.remove);
  const cancelReminder = useMutation(api.reminders.cancelReminder);
  const toggleEvent = useMutation(api.favorites.toggleEvent);
  const [busy, setBusy] = useState<string | null>(null);

  const onRemove = async (favoriteId: string) => {
    setBusy(favoriteId);
    try {
      await remove({ favoriteId: favoriteId as Id<"favorites"> });
    } finally {
      setBusy(null);
    }
  };

  if (data === undefined) {
    return (
      <PageContainer>
        <div className="max-w-2xl mx-auto py-10 space-y-3">
          <div className="h-8 w-40 rounded bg-muted animate-pulse" />
          <div className="h-24 rounded-lg bg-muted animate-pulse" />
          <div className="h-24 rounded-lg bg-muted animate-pulse" />
        </div>
      </PageContainer>
    );
  }

  const reminderRows = (reminders ?? []) as ReminderRow[];
  const total =
    data.events.length +
    data.series.length +
    data.venues.length +
    data.organizers.length +
    reminderRows.length;

  return (
    <PageContainer>
      <div className="max-w-2xl mx-auto py-10">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Star className="w-5 h-5 text-primary" />
            Following
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total === 0
              ? "Nothing yet. Tap the star on any event, venue or organizer to follow it."
              : "Everything you've starred, and what you'll hear about."}
          </p>
        </header>

        <Section
          icon={Repeat}
          title="Series"
          count={data.series.length}
          empty="Follow a repeating event and you'll keep up with it as new dates are added."
        >
          {(data.series as SeriesRow[]).map((s) => (
            <FollowRow
              key={s.favoriteId}
              favoriteId={s.favoriteId}
              href={`/events?event=${s.sampleEventId}`}
              title={s.title}
              badge={
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                  <Repeat className="w-3 h-3" />
                  {s.upcomingCount} upcoming
                </span>
              }
              meta={
                <>
                  <p className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    Next: {formatWhen(s.nextDate, s.startTime)}
                  </p>
                  <p className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    {s.venueName}
                  </p>
                </>
              }
              onRemove={onRemove}
              busy={busy === s.favoriteId}
            />
          ))}
        </Section>

        <Section
          icon={Calendar}
          title="Events"
          count={data.events.length}
          empty="One-off events you star will show up here."
        >
          {(data.events as EventRow[]).map((e) => (
            <FollowRow
              key={e.favoriteId}
              favoriteId={e.favoriteId}
              href={`/events?event=${e.eventId}`}
              title={e.title}
              badge={
                e.isPast ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    Past
                  </span>
                ) : undefined
              }
              meta={
                <>
                  <p className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 shrink-0" />
                    {formatWhen(e.date, e.startTime)}
                  </p>
                  <p className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    {e.venueName}
                  </p>
                </>
              }
              onRemove={onRemove}
              busy={busy === e.favoriteId}
            />
          ))}
        </Section>

        <Section
          icon={MapPin}
          title="Venues"
          count={data.venues.length}
          empty="Follow a venue to hear about closures, cancellations and changes before they catch you out."
        >
          {(data.venues as VenueRow[]).map((v) => (
            <FollowRow
              key={v.favoriteId}
              favoriteId={v.favoriteId}
              href={`/venue/${v.venueId}`}
              title={v.name}
              meta={
                <p className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  {v.address}
                </p>
              }
              onRemove={onRemove}
              busy={busy === v.favoriteId}
            />
          ))}
        </Section>

        <Section
          icon={Building2}
          title="Organizers & studios"
          count={data.organizers.length}
          empty="Follow the people and studios whose events you don't want to miss."
        >
          {(data.organizers as OrganizerRow[]).map((o) => (
            <FollowRow
              key={o.favoriteId}
              favoriteId={o.favoriteId}
              href="/events"
              title={o.name}
              badge={
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground capitalize">
                  {o.accountType}
                </span>
              }
              meta={
                o.studioAddress ? (
                  <p className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    {o.studioAddress}
                  </p>
                ) : null
              }
              onRemove={onRemove}
              busy={busy === o.favoriteId}
            />
          ))}
        </Section>

        <Section
          icon={Bell}
          title="One-off reminders"
          count={reminderRows.length}
          empty="Reminders you set without following anything show up here."
        >
          {reminderRows.map((r) => (
            <li
              key={r.reminderId}
              className="p-4 rounded-lg border border-border bg-card"
            >
              <div className="flex items-start justify-between gap-3">
                <Link href={`/events?event=${r.eventId}`} className="min-w-0 flex-1 group">
                  <p className="font-medium text-foreground group-hover:text-primary transition-colors">
                    {r.title}
                  </p>
                  <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                    <p className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      {formatWhen(r.date, r.startTime)}
                    </p>
                    <p className="flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      {r.venueName}
                    </p>
                  </div>
                </Link>
                <button
                  onClick={async () => {
                    setBusy(r.reminderId);
                    try {
                      await cancelReminder({
                        token: r.token,
                        reminderId: r.reminderId as Id<"eventReminders">,
                      });
                    } finally {
                      setBusy(null);
                    }
                  }}
                  disabled={busy === r.reminderId}
                  className="shrink-0 p-1.5 rounded text-muted-foreground hover:text-destructive transition-colors"
                  aria-label={`Cancel reminder for ${r.title}`}
                  title="Cancel this reminder"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Offered, never assumed: a reminder is about one night, and
                  following the series signs you up for every future date. */}
              {r.patternId && !r.seriesFollowed && (
                <button
                  onClick={async () => {
                    setBusy(r.reminderId);
                    try {
                      await toggleEvent({
                        eventId: r.eventId as Id<"events">,
                      });
                    } finally {
                      setBusy(null);
                    }
                  }}
                  disabled={busy === r.reminderId}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <Repeat className="w-3.5 h-3.5" />
                  This one repeats — follow every date
                </button>
              )}
              {r.patternId && r.seriesFollowed && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Repeat className="w-3.5 h-3.5" />
                  You follow this series
                </p>
              )}
            </li>
          ))}
        </Section>

        {total === 0 && (
          <div className="pt-2">
            <Button asChild>
              <Link href="/events">Find something to follow</Link>
            </Button>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
